<script lang="ts">
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../../editable';
	import SbRichtext from '../../components/sbRichtext.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import type { SbLink } from '../../types';
	import { matchList } from '$lib/utils/geolocation';
	import { goto } from '$app/navigation';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import PageGrid16 from '../../components/pageGrid16.svelte';
	import Blok from '../../blok.svelte';
	import { object, string, regex, safeParse, pipe } from 'valibot';
	import TextInput, { type FormState } from '$lib/input/textInput.svelte';
	import { identify, trackEvent } from '$lib/segment/client';
	import Modal from '$lib/modal/v3/modal.svelte';

	interface OfferMapping {
		zip_codes: string;
		offer_page: SbLink;
	}

	interface Props {
		content: ISbStoryData<{
			asset: ISbStoryData[];
			headline: ISbRichtext;
			subline: string;
			cta_label: string;
			input_placeholder: string;
			no_offer_modal: ISbStoryData[];
			offer_mapping: ISbStoryData<OfferMapping>['content'][];
		}>['content'];
	}

	let { content }: Props = $props();
	let formState: FormState = $state(null);
	let error = $state<string | null>(null);

	let zipCode: undefined | string = $state(undefined);
	let city: undefined | string = $state(undefined);
	let modal: { show: () => void; hide: () => void; remove: () => void };

	const zipCodeSchema = object({
		zipCode: pipe(string(), regex(/^\d{5}(-\d{4})?$/))
	});

	function findOfferPage(zipCode: string) {
		return content.offer_mapping.find((e) => matchList(e.zip_codes, zipCode))?.offer_page;
	}

	//TODO: use server action
	async function handleSubmit(event: SubmitEvent) {
		event.preventDefault();
		formState = 'processing';
		error = '';

		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);
		const formZipCode = formData.get('zipCode');

		const parsedZipCode = safeParse(zipCodeSchema, {
			zipCode: formZipCode?.toString().trim()
		});

		if (!parsedZipCode.success) {
			formState = 'error';
			error = 'Invalid zip code';
			return;
		}

		trackEvent('Zip code entered', {
			zipCode: parsedZipCode.output.zipCode
		});
		identify({
			zipCode: parsedZipCode.output.zipCode
		});

		zipCode = parsedZipCode.output.zipCode.split('-')[0];

		try {
			const response = await fetch('/api/geo/city?zip=' + zipCode);
			if (!response.ok) {
				throw new Error('Error fetching city');
			}
			const data = await response.json();

			city = data.city;
		} catch (error) {
			console.error('No city found:', error);
		}

		const offerPage = findOfferPage(zipCode);

		if (offerPage) {
			trackEvent('Local offer found', {
				zipCode: zipCode
			});

			const params = new URLSearchParams();
			params.set('zip', zipCode);
			if (city) {
				params.set('city', city);
			}

			goto(getSbUrl(offerPage, params));
		} else {
			if (!city) {
				city = zipCode;
			}

			trackEvent('No local offer found', {
				zipCode: zipCode
			});

			modal.show();
		}

		formState = 'success';
	}
</script>

<section
	id="hero"
	use:editable={content}
	class="hero-bg-light h-svh text-brown-1 landscape:min-h-[600px]"
>
	<div class="relative flex h-full flex-col lg:justify-center">
		{#if content.asset[0]}
			<div
				class="max-lg:max-h-[700px] max-lg:flex-1 lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2 lg:pb-5 lg:pr-5 max-lg:landscape:max-h-[250px]"
			>
				<div class="h-full overflow-hidden lg:rounded-xl">
					<Blok content={content.asset[0]} />
				</div>
			</div>
		{/if}
		<PageGrid16 margin>
			<div class="col-span-full max-lg:my-6 lg:col-start-2 lg:col-end-7">
				<div>
					<div
						class="text-[44px]/[108%] font-semibold tracking-[-.44px] lg:text-[80px]/[100%] lg:tracking-[-1.6px]"
					>
						<SbRichtext text={content.headline} />
					</div>
					<p
						class="mt-2 text-[16px]/[150%] tracking-[-.16px] lg:text-[24px]/[140%] lg:tracking-[-.24px]"
					>
						{content.subline}
					</p>
				</div>

				<form class="mt-5" onsubmit={handleSubmit} action="#" method="POST">
					<TextInput
						name="zipCode"
						placeholder={content.input_placeholder}
						autocomplete="postal-code"
					/>

					{#if error}
						<p class="mt-2 text-[12px]/[100%] text-red-500">{error}</p>
					{/if}

					<Button
						class={[
							'btn-large mt-6',
							formState && ['processing'].includes(formState) ? 'opacity-50' : ''
						]}
						disabled={formState === 'processing'}
						type="submit"
					>
						{content.cta_label}
					</Button>
				</form>

				<Modal bind:this={modal} size="fullscreen">
					<Blok {city} content={content.no_offer_modal[0]} />
				</Modal>
			</div>
		</PageGrid16>
	</div>
</section>

<style lang="scss">
	section {
		@media (min-width: 1024px) {
			padding-top: var(--header-height);
			transition-property: padding-top, height;
			transition-duration: 0.2s;
		}
	}
</style>
